import React, { useEffect, useMemo, useState } from 'react';

/**
 * One list for everything a project holds — Stories, Captions and uploaded Shorts — in a single
 * card shape, told apart by a type badge rather than by which section they sit in.
 *
 * A card reports its newest *successful* render: failures belong to the Renders tab, so a card
 * that has ever worked shows the working one, and only a card that has never produced anything
 * admits it. The video rows a story render leaves behind are counted and hidden behind a filter
 * rather than deleted.
 */

export type ItemKind = 'story' | 'caption' | 'shorts' | 'y-ray';

export interface ProjectItem {
  kind: ItemKind;
  id: string;
  title: string;
  thumbnailUrl: string | null;
  slideCount?: number;
  durationSeconds?: number | null;
  clipCount?: number;
  avgScore?: number;
  preset?: string | null;
  publishStatus?: string;
  latestRenderUrl: string | null;
  latestRenderAt: string | null;
  attempts: number;
  status: string;
  stage: string | null;
  progress: number | null;
  isStoryRender: boolean;
  updatedAt: string;
  /** Set on a Shorts item generated from a caption render — the caption it was cut from. */
  sourceCaptionWorkspaceId?: string | null;
  sourceCaptionName?: string | null;
  /** Soft-archived: hidden from the main list, shown only under the Archive chip. */
  archived?: boolean;
}

interface ProjectItemListProps {
  items: ProjectItem[];
  onOpen: (item: ProjectItem) => void;
  onPlay: (item: ProjectItem) => void;
  /** Rendered at the right of a Shorts card, where its own actions live. */
  shortsActions?: (item: ProjectItem) => React.ReactNode;
  /** Opens the Caption a generated Shorts item came from. Absent hides the lineage link. */
  onOpenSourceCaption?: (workspaceId: string) => void;
  /** Archives an item. Absent (shared project) hides both the card button and the Archive chip. */
  onArchive?: (item: ProjectItem) => void;
}

const KIND_LABEL: Record<ItemKind, string> = { story: 'Story', caption: 'Caption', shorts: 'Shorts', 'y-ray': 'Y-Ray' };
/** "Shorts" is already plural and "Story" does not take a bare s. */
const KIND_PLURAL: Record<ItemKind, string> = { story: 'Stories', caption: 'Captions', shorts: 'Shorts', 'y-ray': 'Y-Ray' };
const KIND_GLYPH: Record<ItemKind, string> = { story: '📖', caption: '💬', shorts: '🎬', 'y-ray': '🔎' };

/** What to do about an item that has never produced a video, said per kind. */
const NOT_RENDERED_TIP: Record<ItemKind, string> = {
  caption: 'Nothing has been rendered yet. Open this Caption, pick a style and generate it — the captioned video and its details will show on its page.',
  story: 'Nothing has been rendered yet. Open this Story and generate it — the video and its details will show on its page.',
  shorts: 'No shorts have been cut yet. Open this video and process it — the clips and their details will show on its page.',
  'y-ray': 'Y-Ray has not finished for this video yet.',
};

const TERMINAL = ['completed', 'done', 'failed', 'ready', 'draft', 'pending'];

function relative(iso: string | null): string {
  if (!iso) return '';
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

function duration(secs: number | null | undefined): string {
  if (!secs || secs <= 0) return '';
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** An upload posted as-is: ready to publish, never run through shorts generation. */
function isRawUpload(item: ProjectItem): boolean {
  return item.kind === 'shorts' && (item.status || '').toLowerCase() === 'ready' && !item.clipCount;
}

/** The meta line differs per kind — it is the only part of the card that does. */
function metaLine(item: ProjectItem): string {
  const parts: string[] = [];
  if (item.kind === 'story') {
    parts.push(`${item.slideCount ?? 0} slide${item.slideCount === 1 ? '' : 's'}`);
  } else if (item.kind === 'caption') {
    if (item.preset) parts.push(`${item.preset.replace(/-/g, ' ')} preset`);
  } else if (item.kind === 'y-ray') {
    const d = duration(item.durationSeconds);
    if (d) parts.push(d);
    if (item.attempts > 1) parts.push(`${item.attempts} runs`);
  } else {
    const d = duration(item.durationSeconds);
    if (d) parts.push(d);
    // An upload posted as-is has no generated clips; "0 clips" would read as a failed run.
    if (isRawUpload(item)) parts.push('uploaded video');
    else parts.push(`${item.clipCount ?? 0} clip${item.clipCount === 1 ? '' : 's'}`);
    if (item.avgScore) parts.push(`avg ${item.avgScore.toFixed(1)}/10`);
  }
  if (item.latestRenderAt) parts.push(`rendered ${relative(item.latestRenderAt)}`);
  else parts.push(relative(item.updatedAt));
  return parts.filter(Boolean).join(' · ');
}

/** What the card says about itself, in one vocabulary across all three kinds. */
function state(item: ProjectItem): { label: string; cls: string; progress: number | null } {
  const s = (item.status || '').toLowerCase();
  if (!TERMINAL.includes(s)) {
    return { label: `${item.stage || s}…`, cls: 'video-status-processing', progress: item.progress ?? null };
  }
  if (item.kind === 'y-ray') {
    return s === 'failed'
      ? { label: 'Failed', cls: 'video-status-failed', progress: null }
      : { label: 'Ready', cls: 'video-status-done', progress: null };
  }
  if (isRawUpload(item)) return { label: 'Ready to post', cls: 'video-status-done', progress: null };
  if (item.latestRenderUrl) return { label: 'Ready', cls: 'video-status-done', progress: null };
  // Not a failure: the item simply has no finished render. It reads as one in red, and the
  // attempt count behind it counted transcription jobs too, so it was wrong as well as alarming.
  if (item.attempts > 0) {
    return { label: 'Not rendered', cls: 'video-status-info', progress: null };
  }
  if (item.kind === 'shorts' && s === 'completed') {
    return { label: 'Ready', cls: 'video-status-done', progress: null };
  }
  return { label: s === 'failed' ? 'Failed' : 'Draft', cls: s === 'failed' ? 'video-status-failed' : 'video-status-pending', progress: null };
}

/**
 * The card's picture, or the kind glyph standing in for it.
 *
 * A thumbnail URL that 404s is as common as none at all — a story whose render was cleaned up,
 * an asset that never uploaded — and the browser's broken-image icon says nothing useful, so a
 * load failure falls back to the same glyph an absent URL gets.
 */
const ItemThumb: React.FC<{ kind: ItemKind; url: string | null }> = ({ kind, url }) => {
  const [failed, setFailed] = useState(false);
  // A row can be re-used for a different item as the list re-sorts; a new URL deserves a try.
  useEffect(() => { setFailed(false); }, [url]);

  if (!url || failed) return <span className="pi-thumb-glyph">{KIND_GLYPH[kind]}</span>;
  return <img src={url} alt="" onError={() => setFailed(true)} />;
};

const ProjectItemList: React.FC<ProjectItemListProps> = ({ items, onOpen, onPlay, shortsActions, onOpenSourceCaption, onArchive }) => {
  const [chosenFilter, setKindFilter] = useState<ItemKind | 'all' | 'archive'>('all');
  // The Archive chip only exists for callers that can archive; without it fall back to All.
  const kindFilter = chosenFilter === 'archive' && !onArchive ? 'all' : chosenFilter;
  const [showStoryRenders, setShowStoryRenders] = useState(false);

  const storyRenderCount = useMemo(() => items.filter(i => i.isStoryRender).length, [items]);

  // Archived items never show in the main list, whatever the viewer's role.
  const visible = useMemo(() => items
    .filter(i => showStoryRenders || !i.isStoryRender)
    .filter(i => kindFilter === 'archive' ? !!i.archived : !i.archived && (kindFilter === 'all' || i.kind === kindFilter)),
    [items, kindFilter, showStoryRenders]);

  const counts = useMemo(() => {
    const base = items.filter(i => showStoryRenders || !i.isStoryRender);
    const live = base.filter(i => !i.archived);
    return {
      all: live.length,
      story: live.filter(i => i.kind === 'story').length,
      caption: live.filter(i => i.kind === 'caption').length,
      shorts: live.filter(i => i.kind === 'shorts').length,
      'y-ray': live.filter(i => i.kind === 'y-ray').length,
      archive: base.filter(i => i.archived).length,
    };
  }, [items, showStoryRenders]);

  return (
    <div className="project-items">
      <div className="project-items-bar">
        <div className="project-items-filters" role="tablist">
          {(['all', 'story', 'caption', 'shorts', 'y-ray'] as const).map(k => (
            <button
              key={k}
              role="tab"
              aria-selected={kindFilter === k}
              className={`pi-chip ${kindFilter === k ? 'pi-chip--on' : ''}`}
              onClick={() => setKindFilter(k)}
            >
              {k === 'all' ? 'All' : `${KIND_GLYPH[k]} ${KIND_PLURAL[k]}`}
              <span className="pi-chip-count">{counts[k]}</span>
            </button>
          ))}
          {onArchive && (
            <button
              role="tab"
              aria-selected={kindFilter === 'archive'}
              className={`pi-chip ${kindFilter === 'archive' ? 'pi-chip--on' : ''}`}
              onClick={() => setKindFilter('archive')}
            >
              🗄 Archive
              <span className="pi-chip-count">{counts.archive}</span>
            </button>
          )}
        </div>
        {storyRenderCount > 0 && (
          <label className="pi-toggle" title="Every Generate leaves a rendered video behind; they belong to their Story">
            <input
              type="checkbox"
              checked={showStoryRenders}
              onChange={e => setShowStoryRenders(e.target.checked)}
            />
            Show story renders ({storyRenderCount})
          </label>
        )}
      </div>

      {visible.length === 0 ? (
        <div className="pi-empty">{kindFilter === 'archive' ? 'Nothing archived.' : 'Nothing here yet.'}</div>
      ) : (
        <div className="video-list">
          {visible.map(item => {
            const st = state(item);
            const playable = !!item.latestRenderUrl;
            return (
              <div
                key={`${item.kind}-${item.id}`}
                className="video-row pi-row"
                onClick={() => onOpen(item)}
                style={{ cursor: 'pointer' }}
              >
                <div className={`video-row-thumb pi-thumb pi-thumb--${item.kind}`}>
                  <ItemThumb kind={item.kind} url={item.thumbnailUrl} />
                  {playable && (
                    <button
                      className="pi-play"
                      title="Play the latest render"
                      onClick={(e) => { e.stopPropagation(); onPlay(item); }}
                    >▶</button>
                  )}
                </div>

                <div className="pi-main">
                  <div className="pi-title-row">
                    <h4 className="pi-title">{item.title}</h4>
                    <span className={`pi-badge pi-badge--${item.kind}`}>
                      {KIND_GLYPH[item.kind]} {KIND_LABEL[item.kind]}
                    </span>
                    {item.isStoryRender && <span className="pi-badge pi-badge--render">story render</span>}
                  </div>
                  <p className="pi-meta">{metaLine(item)}</p>
                  {/* A Shorts item cut from a caption render names its origin and links back to
                      it — the return leg of the flow that created it. */}
                  {item.sourceCaptionWorkspaceId && item.sourceCaptionName && (
                    <button
                      className="pi-lineage"
                      title={`Open caption "${item.sourceCaptionName}"`}
                      disabled={!onOpenSourceCaption}
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenSourceCaption?.(item.sourceCaptionWorkspaceId!);
                      }}
                    >
                      ✂ from caption: <span className="pi-lineage-name">{item.sourceCaptionName}</span>
                    </button>
                  )}
                  {st.progress !== null && (
                    <div className="pi-progress"><span style={{ width: `${Math.max(2, st.progress)}%` }} /></div>
                  )}
                </div>

                <div className="pi-side">
                  <div className="pi-state">
                    <span className={`video-status-badge ${st.cls}`}>{st.label}</span>
                    {st.label === 'Not rendered' && (
                      <span
                        className="pi-info"
                        data-tip={NOT_RENDERED_TIP[item.kind]}
                        role="img"
                        aria-label={NOT_RENDERED_TIP[item.kind]}
                        onClick={(e) => e.stopPropagation()}
                      >
                        i
                      </span>
                    )}
                    {item.publishStatus && (
                      <span className={`video-status-badge ${item.publishStatus === 'published' ? 'video-status-done' : 'video-status-pending'}`}>
                        {item.publishStatus === 'published' ? '🌐 Published' : '📝 Unpublished'}
                      </span>
                    )}
                  </div>
                  {/* Only the controls swallow the click; the rest of the column opens the row
                      like everywhere else, so there is no dead area to land in. */}
                  <div className="pi-actions" onClick={(e) => e.stopPropagation()}>
                    <button className="btn-sm" onClick={() => onOpen(item)}>Open</button>
                    {playable && (
                      <button className="btn-sm btn-sm-accent" onClick={() => onPlay(item)}>▶ Play latest</button>
                    )}
                    {item.kind === 'shorts' && shortsActions?.(item)}
                    {onArchive && !item.archived && (
                      <button
                        className="btn-sm pi-archive-btn"
                        title="Archive — moves this out of the main list"
                        aria-label={`Archive ${item.title}`}
                        onClick={() => onArchive(item)}
                      >🗄</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProjectItemList;
